import React, { useState, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { toast } from "sonner";
import { Package, RefreshCw, Loader2 } from "lucide-react";
import { AvailableDeliveryCard, AssignedDeliveryCard } from "./deliveries";

export default function LocalDeliveries() {
  const [deliveries, setDeliveries] = useState([]);
  const [availableDeliveries, setAvailableDeliveries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [claiming, setClaiming] = useState(null);
  const [declining, setDeclining] = useState(null);
  const [activeTab, setActiveTab] = useState("assigned");

  const fetchDeliveries = useCallback(async () => {
    try {
      const response = await api.get("/local-delivery/partner");
      setDeliveries(response.data);
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('Failed to fetch deliveries:', error.message);
      }
      setDeliveries([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchAvailableDeliveries = useCallback(async () => {
    try {
      const response = await api.get("/local-delivery/available");
      setAvailableDeliveries(response.data);
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('Failed to fetch available deliveries:', error.message);
      }
      setAvailableDeliveries([]);
    }
  }, []);

  useEffect(() => {
    fetchDeliveries();
    fetchAvailableDeliveries();
  }, [fetchDeliveries, fetchAvailableDeliveries]);

  const handleClaimDelivery = async (deliveryId) => {
    setClaiming(deliveryId);
    try {
      await api.post(`/local-delivery/${deliveryId}/claim`);
      toast.success("Delivery claimed successfully!");
      fetchDeliveries();
      fetchAvailableDeliveries();
      setActiveTab("assigned");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to claim delivery");
    } finally {
      setClaiming(null);
    }
  };

  const handleDeclineDelivery = async (deliveryId) => {
    if (!window.confirm("Are you sure you want to decline this delivery? It will be reassigned to another partner.")) {
      return;
    }
    
    setDeclining(deliveryId);
    try {
      await api.post(`/local-delivery/${deliveryId}/decline`);
      toast.success("Delivery declined. It will be reassigned to another partner.");
      fetchDeliveries();
      fetchAvailableDeliveries();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to decline delivery");
    } finally {
      setDeclining(null);
    }
  };

  const handleUpdateStatus = async (deliveryId, newStatus) => {
    try {
      if (newStatus === "delivered") {
        await api.post(`/local-delivery/${deliveryId}/mark-delivered`);
      } else {
        await api.post(`/local-delivery/${deliveryId}/status`, { status: newStatus });
      }
      toast.success(`Delivery marked as ${newStatus}`);
      fetchDeliveries();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update status");
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-10">
        <Loader2 className="w-6 h-6 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-black tracking-tight">Local Deliveries</h1>
        <Button 
          variant="outline" 
          onClick={() => { fetchDeliveries(); fetchAvailableDeliveries(); }} 
          className="rounded-none gap-2"
        >
          <RefreshCw className="w-4 h-4" />
          Refresh
        </Button>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-border">
        <button
          onClick={() => setActiveTab("assigned")}
          className={`px-4 py-2 font-medium transition-colors ${
            activeTab === "assigned" 
              ? "border-b-2 border-accent text-accent" 
              : "text-muted-foreground hover:text-foreground"
          }`}
          data-testid="assigned-tab"
        >
          My Deliveries ({deliveries.length})
        </button>
        <button
          onClick={() => setActiveTab("available")}
          className={`px-4 py-2 font-medium transition-colors ${
            activeTab === "available" 
              ? "border-b-2 border-accent text-accent" 
              : "text-muted-foreground hover:text-foreground"
          }`}
          data-testid="available-tab"
        >
          Available Pickups ({availableDeliveries.length})
        </button>
      </div>

      {/* Available Deliveries Tab */}
      {activeTab === "available" && (
        <>
          {availableDeliveries.length === 0 ? (
            <EmptyState 
              message="No available pickups" 
              subMessage="Make sure you're online to see nearby deliveries" 
            />
          ) : (
            <div className="space-y-4">
              {availableDeliveries.map((delivery) => (
                <AvailableDeliveryCard
                  key={delivery.id}
                  delivery={delivery}
                  onClaim={handleClaimDelivery}
                  claiming={claiming}
                />
              ))}
            </div>
          )}
        </>
      )}

      {/* Assigned Deliveries Tab */}
      {activeTab === "assigned" && (
        <>
          {deliveries.length === 0 ? (
            <EmptyState 
              message="No assigned deliveries" 
              subMessage='Check "Available Pickups" tab to claim new deliveries' 
            />
          ) : (
            <div className="space-y-4">
              {deliveries.map((delivery) => (
                <AssignedDeliveryCard
                  key={delivery.id}
                  delivery={delivery}
                  onUpdateStatus={handleUpdateStatus}
                  onDecline={handleDeclineDelivery}
                  declining={declining}
                />
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function EmptyState({ message, subMessage }) {
  return (
    <div className="text-center py-10 border border-border">
      <Package className="w-12 h-12 mx-auto text-muted-foreground/30 mb-4" />
      <p className="text-muted-foreground mb-2">{message}</p>
      <p className="text-sm text-muted-foreground">{subMessage}</p>
    </div>
  );
}
